import { useEffect } from 'react';
import { Card, CardContent } from '@mui/material';
import {
  Datagrid,
  List,
  FunctionField,
  Button,
  required,
  Filter,
  SelectInput,
  useListContext,
} from 'react-admin';
import { parseCronExpression } from '../../utils';
import { IndexField, CustomActions } from '../../components';
import { useCognitoUser } from '../../hooks';

const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getValidDates = () => {
  const dates = [];
  const today = new Date();

  // Add past 1 day
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  dates.push(yesterday);

  // Add today + next 4 days
  for (let i = 0; i < 5; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }

  // Use local date string for id so it matches convertToAWSUTC's local-date interpretation
  return dates.map(date => ({
    id: toLocalDateStr(date),
    name: date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, ' '),
  }));
};

const ListFilters = () => {
  const dateChoices = getValidDates();
  const { filterValues, setFilters } = useListContext();

  useEffect(() => {
    const validIds = dateChoices.map(d => d.id);
    if (!filterValues?.selected_date || !validIds.includes(filterValues.selected_date)) {
      setFilters({ selected_date: toLocalDateStr(new Date()) });
    }
  }, []);

  return (
    <Filter>
      <SelectInput
        label="Select Date"
        source="selected_date"
        choices={dateChoices}
        alwaysOn
        validate={required()}
      />
    </Filter>
  );
};

const PatrolStartButton = ({ record, currentUser }) => {
  const { filterValues } = useListContext();

  const isCompleted = !!record.completed;

  const cronParsed = parseCronExpression(record.schedule?.cron_expression);
  const timeSort = cronParsed?.time_sort; // minutes from midnight

  // selected_date => "YYYY-MM-DD"
  const selectedDateStr = filterValues?.selected_date;

  const isAfterScheduleTime = (() => {
    // if cron parsing is err, it's false as a disabled
    if (timeSort == null || !selectedDateStr) return false;

    const [y, m, d] = selectedDateStr.split("-").map(Number);
    const hh = Math.floor(timeSort / 60);
    const mm = timeSort % 60;

    // create date for the scheduled time based on the local timezone
    const scheduledDateTime = new Date(y, m - 1, d, hh, mm, 0, 0);
    return new Date() >= scheduledDateTime;
  })();

  const disabled = isCompleted || !isAfterScheduleTime;

  const handlePatrolView = () => {
    const checkpoints = record.schedule.route.route_checkpoints.items;
    const decodedData = {
      patrol_id: record.id,
      checkpoint_ids:
        checkpoints.sort((a, b) => a.order - b.order).map((i) => i.checkpoint_id) || [],
      route_name: record.schedule.route.name,
      schedule_id: record.schedule_id,
      operator_id: currentUser.id,
      start_time: new Date().toISOString(),
    };

    const encodedData = encodeURIComponent(btoa(JSON.stringify(decodedData)));
    const url = `#/patrol-view?data=${encodedData}`;
    window.open(
      url,
      "Patrol View",
      `toolbar=0,menubar=0,width=${(window.screen.availWidth / 20) * 19},height=${(window.screen.availHeight / 20) * 18}`
    );
  };

  return (
    <Button
      variant="contained"
      label={`${isCompleted ? 'Done' : 'START'}`}
      onClick={handlePatrolView}
      disabled={disabled}
    />
  );
};

const ListOverview = () => {
  const currentUser = useCognitoUser();
  const defaultSelectedDate = toLocalDateStr(new Date());

  return (
    <Card variant='elevation'>
      <CardContent>
      <List actions={<CustomActions />} filters={<ListFilters />} filterDefaultValues={{ selected_date: defaultSelectedDate }} >
          <Datagrid size='medium' bulkActionButtons={false} sx={{ borderTop: '1px solid grey', maxHeight: '70vh', overflow: 'auto' }}>
            <FunctionField
              label='NO'
              render={(record) => <IndexField record={record} />}
            />
            <FunctionField
              label='TIME'
              render={(record) => parseCronExpression(record.schedule.cron_expression)?.time || '-'}
            />
            <FunctionField
              label='ROUTE'
              render={(record) => record.schedule.route.name}
            />
            <FunctionField
              label='CHECKPOINTS'
              render={(record) => record.schedule.route.route_checkpoints.items.length}
            />
            <FunctionField
              label="ACTION"
              render={(record) => <PatrolStartButton record={record} currentUser={currentUser} />}
            />
          </Datagrid>
        </List>
      </CardContent>
    </Card>
  )
};

export default ListOverview;
export { ListOverview };